import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { calendarRefreshDelay, resolveConfiguredCalendarTheme } from "../../lib/calendarThemes";
import "./calendarTheme.css";

const CalendarThemeContext = createContext(null);

export const useCalendarTheme = () => useContext(CalendarThemeContext);

export function CalendarThemeProvider({ children }) {
  // Identical server/first client render; browser-only scheduling after mount.
  const [theme, setTheme] = useState(null);
  const [mode, setMode] = useState("off");
  useEffect(() => {
    let alive = true;
    let pending = false;
    let revision = 0;
    const load = async () => {
      if (pending || document.visibilityState === "hidden") return;
      pending = true;
      const requestedRevision = revision;
      try {
        const { data } = await axios.get(`${API}/public/appearance`, { timeout: 8000 });
        if (alive && requestedRevision === revision) setMode(typeof data?.calendar_theme === "string" ? data.calendar_theme : "off");
      } catch {
        // Preserve the last successful setting during a temporary outage.
      } finally { pending = false; }
    };
    const onSaved = (event) => {
      if (typeof event.detail?.calendar_theme === "string") {
        revision += 1;
        setMode(event.detail.calendar_theme);
      }
      else load();
    };
    load();
    const poll = setInterval(load, 60000);
    document.addEventListener("visibilitychange", load);
    window.addEventListener("focus", load);
    window.addEventListener("appearance-saved", onSaved);
    return () => {
      alive = false;
      clearInterval(poll);
      document.removeEventListener("visibilitychange", load);
      window.removeEventListener("focus", load);
      window.removeEventListener("appearance-saved", onSaved);
    };
  }, []);
  useEffect(() => {
    let timer;
    const refresh = () => {
      clearTimeout(timer);
      const now = new Date();
      setTheme(resolveConfiguredCalendarTheme(mode, now));
      timer = setTimeout(refresh, calendarRefreshDelay(now));
    };
    refresh();
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [mode]);
  return <CalendarThemeContext.Provider value={theme}>
    <div data-calendar-theme={theme?.id} data-calendar-phase={theme?.phase}>{children}</div>
  </CalendarThemeContext.Provider>;
}

export function CalendarThemeBadge() {
  const theme = useContext(CalendarThemeContext);
  if (!theme) return null;
  return <p className="calendar-theme-badge">
    <svg viewBox="0 0 40 28" width="30" height="21" aria-hidden="true">
      <rect width="40" height="28" rx="3" fill="#c8102e" />
      <path fill="white" d="M23 7a8 8 0 1 0 0 14 7 7 0 1 1 0-14Z" />
      <path fill="white" d="m27 9 1.2 3.5H32l-3 2.2 1.1 3.5-3.1-2.1-3.1 2.1 1.1-3.5-3-2.2h3.8Z" />
    </svg>
    <span>{theme.label}</span>
  </p>;
}
